import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../../../../../../store';
import {
  getRankedCryoboxesThunk,
  rankedCryoboxesIsBusySelector,
  rankedCryoboxesResponseSelector,
} from '../../../../../../../../store/stagedShipmentCryoboxRanked';
import { ButtonRow, Loader, theme } from '@omnigenbiodata/ui';
import {
  addCryoboxTostagingReset,
  addCryoboxToStagingThunk,
} from '../../../../../../../../store/stagedShipmentCryoboxAdd';
import { RankedCryobox } from '../../../../../../../../core/api/lab.types';
import {
  addCryoboxToStagingHasErrorSelector,
  addCryoboxToStagingIsBusySelector,
  addCryoboxToStagingResponseSelector,
} from '../../../../../../../../store/stagedShipmentCryoboxAdd/selectors';
import StatusMessage from '../../../../../../../../components/StatusMessage';

interface ViewSuggestedBoxesModalProps {
  eligibleSamplesCount: number;
  totalBoxes: number;
  isOpen: boolean;
  onClose: () => void;
}

const ViewSuggestedBoxesModal = ({
  eligibleSamplesCount,
  totalBoxes,
  isOpen,
  onClose,
}: ViewSuggestedBoxesModalProps) => {
  const { t } = useTranslation('portal');
  const dispatch = useAppDispatch();
  const [addedSuccess, setAddedSuccess] = useState<any>({ showMsg: false });
  const suggestedBoxes: RankedCryobox[] = useAppSelector(rankedCryoboxesResponseSelector) || [];
  const suggestedBoxesBusy = useAppSelector(rankedCryoboxesIsBusySelector);
  const addCryoboxToStaging = useAppSelector(addCryoboxToStagingResponseSelector);
  const addCryoboxToStagingBusy = useAppSelector(addCryoboxToStagingIsBusySelector);
  const addCryoboxToStagingIsError = useAppSelector(addCryoboxToStagingHasErrorSelector);

  const handleAdd = async (record: any) => {
    dispatch(addCryoboxToStagingThunk({ cryoboxBarcode: null, cryoboxUuid: record.cryoboxUuid }));
    setAddedSuccess((prev: any) => ({
      ...prev,
      showMsg: false,
      serialNo: record.serialNo,
    }));
  };

  useEffect(() => {
    if (!isOpen || !addCryoboxToStaging?.cryoboxUuid) return;

    dispatch(getRankedCryoboxesThunk());
    dispatch(addCryoboxTostagingReset());

    setAddedSuccess({
      showMsg: true,
      serialNo: addedSuccess.serialNo,
    });
  }, [addCryoboxToStaging, isOpen, dispatch, addedSuccess.serialNo]);

  useEffect(() => {
    if (!addedSuccess.showMsg) return;

    const timer = setTimeout(() => {
      setAddedSuccess((prev: any) => ({
        ...prev,
        showMsg: false,
      }));
    }, 5000);

    return () => clearTimeout(timer);
  }, [addedSuccess.showMsg]);

  useEffect(() => {
    if (isOpen) {
      dispatch(getRankedCryoboxesThunk());
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(addCryoboxTostagingReset());
    };
  }, [dispatch]);

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent>
        <Box mb={3}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Suggested Boxes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The boxes below are recommended for this shipment based on participant eligibility, sample suitability,
            compatibility with other samples in the shipment, and previous shipment history.
          </Typography>
          <br />

          <StatusMessage
            severity="error"
            show={addCryoboxToStagingIsError.hasError}
            message={addCryoboxToStagingIsError.message}
            onClose={() => {
              dispatch(addCryoboxTostagingReset());
            }}
          />

          <StatusMessage
            severity="success"
            show={addedSuccess && addedSuccess.showMsg}
            message={`Box (${addedSuccess.serialNo}) has been added successfully.`}
            onClose={() => {
              dispatch(addCryoboxTostagingReset());
            }}
          />

          <Stack direction="row" spacing={4} mt={1}>
            <Typography variant="body1">
              Total Eligible Samples:{' '}
              <Box
                component="span"
                sx={{ color: theme.palette.success.main, fontWeight: 700 }}
                data-testid="staged-aliquot-count"
              >
                {eligibleSamplesCount ?? 0}
              </Box>
            </Typography>

            <Typography variant="body1">
              Total Boxes:{' '}
              <Box
                component="span"
                sx={{ color: theme.palette.info.dark, fontWeight: 700 }}
                data-testid="staged-boxes-count"
              >
                {totalBoxes ?? 0}
              </Box>
            </Typography>
          </Stack>
        </Box>

        <TableContainer>
          <Table
            sx={{
              borderCollapse: 'separate',
              borderSpacing: '0 12px',
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: 'grey.100',
                }}
              >
                <TableCell width={56} />
                <TableCell sx={{ fontWeight: 700 }}>Box Serial No</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  Sample Type
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  Samples
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  Eligible
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  Ineligible
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {suggestedBoxes.map((record, suggestedBoxIndex) => {
                return (
                  <TableRow
                    key={record.serialNo}
                    data-testid={`suggested-box-${suggestedBoxIndex}`}
                    sx={{
                      '& td': {
                        backgroundColor: record.eligible ? 'background.paper' : '#FFF8E6',
                        borderTop: `1px solid ${!record.eligible ? '#FECACA' : '#E5E7EB'}`,
                        borderBottom: `1px solid ${!record.eligible ? '#FECACA' : '#E5E7EB'}`,
                        py: 1.2,
                      },

                      '& td:first-of-type': {
                        borderLeft: `1px solid ${!record.eligible ? '#FECACA' : '#E5E7EB'}`,
                        borderTopLeftRadius: 5,
                        borderBottomLeftRadius: 5,
                      },

                      '& td:last-of-type': {
                        borderRight: `1px solid ${!record.eligible ? '#FECACA' : '#E5E7EB'}`,
                        borderTopRightRadius: 5,
                        borderBottomRightRadius: 5,
                      },
                    }}
                  >
                    <TableCell data-testid={`suggested-box-${suggestedBoxIndex}-icon`}>
                      {record.eligible ? (
                        <CheckCircleOutlineIcon
                          color="success"
                          data-testid={`suggested-box-${suggestedBoxIndex}-icon-success`}
                        />
                      ) : (
                        <WarningAmberIcon
                          sx={{ color: '#F59E0B' }}
                          data-testid={`suggested-box-${suggestedBoxIndex}-icon-warning`}
                        />
                      )}
                    </TableCell>

                    <TableCell data-testid={`suggested-box-${suggestedBoxIndex}-boxId`}>{record.serialNo}</TableCell>
                    <TableCell align="center" data-testid={`suggested-box-${suggestedBoxIndex}-type`}>
                      {record.sampleType}
                    </TableCell>

                    <TableCell align="center" data-testid={`suggested-box-${suggestedBoxIndex}-total`}>
                      <Typography fontWeight={700}>{record.eligibleCount + record.ineligibleCount}</Typography>
                    </TableCell>

                    <TableCell align="center" data-testid={`suggested-box-${suggestedBoxIndex}-eligible`}>
                      <Typography fontWeight={700} color="success.main">
                        {record.eligibleCount}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <Typography
                        fontWeight={700}
                        color={record.ineligibleCount > 0 ? 'error.main' : 'text.secondary'}
                        data-testid={`suggested-box-${suggestedBoxIndex}-ineligible`}
                      >
                        {record.ineligibleCount}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Button
                        className="btnSuggestedBoxAdd"
                        variant="contained"
                        size="small"
                        onClick={() => handleAdd(record)}
                        disabled={Boolean(suggestedBoxesBusy)}
                      >
                        ADD
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}

              {suggestedBoxes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary">No suggested boxes available.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Loader isVisible={suggestedBoxesBusy} label={t(`Loading...`) as string} />
        <Loader isVisible={addCryoboxToStagingBusy} label={t(`Adding Cryobox to staging...`) as string} />
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: 'flex-start',
          px: 3,
          pb: 3,
        }}
      >
        <ButtonRow
          buttonSize="large"
          backLabel={t('buttons.close') as string}
          showForward={false}
          onBack={onClose}
          forwardColor="primary"
          backIcon={null}
        />
      </DialogActions>
    </Dialog>
  );
};

export default ViewSuggestedBoxesModal;
